import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Phone, AlertTriangle, Heart, Shield, Clock, X } from "lucide-react";

/**
 * Crisis Mode Header Component
 * High-contrast Emergency Help button for hospice care website
 * Designed for accessibility for older adults
 */
export default function CrisisHeader() {
  const [showEmergency, setShowEmergency] = useState(false);

  return (
    <>
      {/* Crisis Header Bar */}
      <div 
        className="bg-gradient-to-r from-[#4A7C6F] to-[#6B9B8A] text-white"
        data-testid="crisis-header"
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Left: Support Message */}
            <div className="flex items-center gap-2 text-sm">
              <Heart className="w-4 h-4 text-rose-200" />
              <span className="hidden sm:inline text-white/90">24/7 Compassionate Care</span>
            </div>

            {/* Center: Main Phone */}
            <a 
              href="tel:703-578-7108" 
              className="flex items-center gap-2 text-sm hover:text-white/90 transition-colors"
              data-testid="main-phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">703-578-7108</span>
            </a>

            {/* Right: Emergency Help Button */}
            <Button
              onClick={() => setShowEmergency(true)}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 py-2 rounded-full shadow-lg border-2 border-rose-400 transition-all hover:scale-105 text-sm sm:text-base"
              data-testid="emergency-help-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Emergency Help
            </Button>
          </div>
        </div>
      </div>

      {/* Emergency Dialog */}
      <Dialog open={showEmergency} onOpenChange={setShowEmergency}>
        <DialogContent className="max-w-md bg-white border-2 border-rose-200">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <DialogTitle className="text-xl text-slate-800">Need Immediate Help?</DialogTitle>
                <DialogDescription className="text-slate-600">
                  We&apos;re here for you 24 hours a day, 7 days a week
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Life-threatening Emergency */}
            <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
              <h4 className="font-bold text-rose-700 flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5" />
                Life-Threatening Emergency
              </h4>
              <p className="text-rose-600 mt-1 text-sm">
                If someone is in immediate danger or experiencing a medical emergency
              </p>
              <a href="tel:911">
                <Button className="w-full mt-3 bg-rose-600 hover:bg-rose-700 text-white text-lg py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 911
                </Button>
              </a>
            </div>

            {/* Hospice Urgent Line */}
            <div className="p-4 bg-[#E8F4F0] rounded-xl border border-[#6B9B8A]">
              <h4 className="font-bold text-[#4A7C6F] flex items-center gap-2 text-lg">
                <Heart className="w-5 h-5" />
                Hospice Urgent Care Line
              </h4>
              <p className="text-[#5A8B7E] mt-1 text-sm">
                For urgent hospice needs, symptom concerns, or caregiver support
              </p>
              <a href="tel:703-578-7108">
                <Button className="w-full mt-3 bg-[#4A7C6F] hover:bg-[#3D6B5F] text-white text-lg py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 703-578-7108
                </Button>
              </a>
              <p className="text-center text-xs text-[#5A8B7E] mt-2">
                Available 24 hours a day, 7 days a week
              </p>
            </div>

            {/* Additional Resources */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <Clock className="w-5 h-5 mx-auto text-slate-500 mb-1" />
                <p className="text-xs text-slate-600 font-medium">24/7 On-Call</p>
                <p className="text-xs text-slate-500">Always available</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <Shield className="w-5 h-5 mx-auto text-slate-500 mb-1" />
                <p className="text-xs text-slate-600 font-medium">Confidential</p>
                <p className="text-xs text-slate-500">HIPAA Protected</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEmergency(false)}
              className="w-full"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
